import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../../api";
import { Shield, ShieldAlert, Monitor, Gamepad2, Link2 } from "lucide-react";
import { toast } from "sonner";

function buildGuardWsUrl(matchId) {
  const backend = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
  const wsBase = backend.startsWith("https://")
    ? backend.replace("https://", "wss://")
    : backend.replace("http://", "ws://");
  return `${wsBase}/api/ws/matches/${matchId}/guard`;
}

function statusChip(player) {
  if (player.status === "guard_active") {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
        🟢 Rivals Guard Active
      </span>
    );
  }
  if (player.status === "console_validated") {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30">
        🎮 Console Validated
      </span>
    );
  }
  return (
    <span className="text-[10px] px-2 py-0.5 rounded bg-destructive/15 text-destructive border border-destructive/40">
      🔴 Rivals Guard Inactive
    </span>
  );
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function RivalsGuardPanel({ matchId, user, isStaff, onStatusChange, clanAName, clanBName }) {
  const [status, setStatus] = useState({
    players: [],
    all_pc_ready: true,
    pc_required_count: 0,
    pc_ready_count: 0,
  });
  const [connecting, setConnecting] = useState(false);
  const [lastLaunchUri, setLastLaunchUri] = useState("");
  const [showLaunchFallback, setShowLaunchFallback] = useState(false);

  // Tracks confirmation received from either polling or WebSocket during launch.
  const guardConfirmedRef = useRef(false);
  const connectionWarningToastId = `rivalsguard-connect-warning-${matchId}`;

  const isMyGuardActive = useCallback(
    (data) => {
      const myUserId = String(user?.id ?? "").trim();
      if (!myUserId) return false;

      const me = (data?.players || []).find(
        (player) => String(player?.user_id ?? "").trim() === myUserId
      );

      return Boolean(
        me?.guard_active === true || me?.status === "guard_active"
      );
    },
    [user?.id]
  );

  const applyStatus = useCallback(
    (data) => {
      const next = data || {
        players: [],
        all_pc_ready: true,
        pc_required_count: 0,
        pc_ready_count: 0,
      };

      setStatus(next);

      // A successful status update can arrive from polling or WebSocket.
      // Normalize user IDs because one side may return a number and the other a string.
      if (isMyGuardActive(next)) {
        guardConfirmedRef.current = true;
        setShowLaunchFallback(false);
        toast.dismiss(connectionWarningToastId);
      }

      onStatusChange?.(next);
      return next;
    },
    [connectionWarningToastId, isMyGuardActive, onStatusChange]
  );

  useEffect(() => {
    let stopped = false;
    let timer;
    const controller = new AbortController();

    const poll = async () => {
      try {
        const { data } = await api.get(`/matches/${matchId}/guard/status`, {
          timeout: 5000,
          signal: controller.signal,
        });
        if (!stopped) applyStatus(data);
      } catch {
        // Keep retrying after temporary network failures.
      } finally {
        // Schedule only after the previous periodic request has finished.
        if (!stopped) timer = setTimeout(poll, 4000);
      }
    };

    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [matchId, applyStatus]);

  useEffect(() => {
    const wsUrl = buildGuardWsUrl(matchId);
    let ws;

    try {
      ws = new WebSocket(wsUrl);
    } catch {
      return undefined;
    }

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data || "{}");

        if (payload.event === "guard_status") {
          applyStatus({
            players: payload.players || [],
            all_pc_ready: !!payload.all_pc_ready,
            pc_required_count: Number(payload.pc_required_count || 0),
            pc_ready_count: Number(payload.pc_ready_count || 0),
          });
        }

        if (payload.event === "guard_red_alert") {
          toast.error(`🚨 Red Alert: ${payload.title || "Guard Alert"}`);
        }
      } catch {
        // Ignore malformed WebSocket packets.
      }
    };

    const heartbeat = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send("ping");
    }, 12000);

    return () => {
      clearInterval(heartbeat);
      if (ws && ws.readyState <= 1) ws.close();
    };
  }, [matchId, applyStatus]);

  const myPlatform = useMemo(() => {
    const p = (user?.gaming_platform || "pc").toLowerCase();
    if (["ps5", "xbox", "console"].includes(p)) return p;
    return "pc";
  }, [user?.gaming_platform]);

  const triggerProtocol = (uri) => {
    if (!uri || !uri.startsWith("rivalsguard://")) {
      throw new Error("Invalid RivalsGuard launch URI");
    }
    window.location.href = uri;
  };

  const waitForMyGuardConnection = useCallback(async () => {
    // Allow up to ~30 seconds for protocol launch, connect and heartbeat.
    // WebSocket confirmation is accepted through guardConfirmedRef as well.
    for (let attempt = 0; attempt < 20; attempt += 1) {
      if (guardConfirmedRef.current) {
        return true;
      }

      await sleep(1500);

      try {
        const { data } = await api.get(`/matches/${matchId}/guard/status`, {
          timeout: 5000,
        });

        applyStatus(data);

        if (isMyGuardActive(data) || guardConfirmedRef.current) {
          return true;
        }
      } catch {
        // WebSocket may still confirm the connection during this period.
      }
    }

    return guardConfirmedRef.current;
  }, [matchId, applyStatus, isMyGuardActive]);

  const launchGuard = async () => {
    if (connecting) return;

    guardConfirmedRef.current = false;
    setConnecting(true);
    setShowLaunchFallback(false);
    toast.dismiss(connectionWarningToastId);

    // Keep a local copy so the fallback remains available even before
    // React finishes updating lastLaunchUri.
    let requestedLaunchUri = lastLaunchUri;

    try {
      // The website JWT/cookie authenticates this request. The server returns a
      // separate short-lived RivalsGuard session token for the desktop client.
      const { data } = await api.get(`/guard/launcher-link/${matchId}`, {
        timeout: 10000,
      });

      const launchUri = data?.uri;
      if (!launchUri || !launchUri.startsWith("rivalsguard://")) {
        throw new Error("Server did not return a valid RivalsGuard URI");
      }

      requestedLaunchUri = launchUri;
      setLastLaunchUri(launchUri);
      triggerProtocol(launchUri);
      toast.success("جاري فتح RivalsGuard على جهازك...");

      const connected = await waitForMyGuardConnection();
      if (connected) {
        setShowLaunchFallback(false);
        toast.dismiss(connectionWarningToastId);
        toast.success("تم تفعيل RivalsGuard وربط جهازك بالمباراة.");
      } else {
        setShowLaunchFallback(true);
        toast.warning(
          "لم يؤكد RivalsGuard الاتصال بعد. استخدم الفتح اليدوي إذا لم يظهر التطبيق.",
          { id: connectionWarningToastId }
        );
      }
    } catch (err) {
      // Do NOT open a tokenless fallback URI. Without a valid token the client
      // must not be allowed to register itself against the match.
      setShowLaunchFallback(Boolean(requestedLaunchUri));
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "تعذر تشغيل RivalsGuard"
      );
    } finally {
      setConnecting(false);
    }
  };

  const openManualGuard = () => {
    if (!lastLaunchUri) {
      toast.error("لا يوجد رابط حماية صالح. اضغط تفعيل الحماية مرة أخرى.");
      return;
    }
    triggerProtocol(lastLaunchUri);
  };

  const myNeedsGuard = myPlatform === "pc";
  const guardReadyText = `${status.pc_ready_count}/${status.pc_required_count}`;

  return (
    <div className="bg-surface border b-soft rounded-xl p-4 space-y-3" data-testid="rivals-guard-panel">
      <div className="flex items-center gap-2">
        {status.all_pc_ready ? (
          <Shield className="text-emerald-400" size={18} />
        ) : (
          <ShieldAlert className="text-destructive" size={18} />
        )}
        <h3 className="font-display font-black text-base">Rivals Guard Lobby</h3>
        <span
          className={`text-xs px-2 py-0.5 rounded border ${
            status.all_pc_ready
              ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10"
              : "text-destructive border-destructive/40 bg-destructive/10"
          }`}
        >
          PC Ready: {guardReadyText}
        </span>
      </div>

      {!status.all_pc_ready && (
        <div className="text-xs rounded-md border border-destructive/35 bg-destructive/10 text-destructive px-3 py-2">
          لا يمكن بدء/متابعة الجولة حتى تصبح حماية كل لاعبي PC فعّالة.
        </div>
      )}

      {myNeedsGuard && (
        <button
          onClick={launchGuard}
          disabled={connecting}
          className="px-3 py-2 rounded-md bg-emerald-500 text-black text-sm font-bold hover:bg-emerald-400 flex items-center justify-center gap-2 w-full disabled:opacity-60 transition"
          data-testid="guard-launch-btn"
        >
          <Link2 size={16} /> {connecting ? "جاري فتح وربط التطبيق..." : "تفعيل الحماية (RivalsGuard)"}
        </button>
      )}

      {myNeedsGuard && showLaunchFallback && (
        <div className="rounded-md border border-amber-500/35 bg-amber-500/10 px-3 py-3 space-y-2">
          <div className="text-xs text-amber-200">
            إذا لم يفتح التطبيق، افتحه يدويًا. في أول استخدام قد يطلب المتصفح تأكيد فتح RivalsGuard.
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={openManualGuard}
              className="px-3 py-1.5 rounded-md bg-amber-400 text-black text-xs font-bold hover:bg-amber-300"
              disabled={!lastLaunchUri}
            >
              افتح RivalsGuard يدوياً
            </button>

            <a
              href="/rivalsguard"
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-md bg-slate-700 text-white text-xs font-bold hover:bg-slate-600"
            >
              تحميل البرنامج / ملف الـ Reg
            </a>
          </div>

          {lastLaunchUri && (
            <div className="rounded bg-black/40 p-2 border border-white/10 space-y-1.5 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gold-400 font-bold">رابط الحماية الخاص بك:</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(lastLaunchUri);
                    toast.success("تم نسخ رابط الحماية.");
                  }}
                  className="text-[10px] bg-gold-500 text-black px-2 py-0.5 rounded font-black hover:bg-gold-400 transition"
                >
                  📋 نسخ الرابط
                </button>
              </div>
              <div className="text-[10px] text-white/55 break-all font-mono select-all">
                {lastLaunchUri}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2 max-h-56 overflow-y-auto">
        {(status.players || []).map((p) => (
          <div
            key={p.user_id}
            className="flex items-center gap-2 rounded-md border b-soft bg-background px-3 py-2 text-sm"
          >
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{p.username}</div>
              <div className="text-[10px] text-white/45">{String(p.side || "").toUpperCase() === "A" ? (clanAName || "الكلان الأول") : String(p.side || "").toUpperCase() === "B" ? (clanBName || "الكلان الثاني") : "اللوبي"}</div>
            </div>
            <div className="text-white/60">
              {p.platform === "pc" ? <Monitor size={14} /> : <Gamepad2 size={14} />}
            </div>
            {statusChip(p)}
          </div>
        ))}

        {(status.players || []).length === 0 && (
          <div className="text-xs text-white/45">لا توجد بيانات حماية حالياً.</div>
        )}
      </div>

      {isStaff && (
        <div className="text-[10px] text-white/45">
          تنبيه: عند اكتشاف تهديد، سيرسل النظام Red Alert فقط بدون حظر تلقائي.
        </div>
      )}
    </div>
  );
}
